<script setup lang="ts">
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";
import { useRoute } from "vue-router";
import { tryOnBeforeMount } from "@vueuse/core";
import { useUiStore } from "@/stores/ui";
import { ref, watch } from "vue";
import { ThemeScroller } from "@/ui-components";
import { useMatomo } from "@/composables/useMatomo";
import { useEventBus } from "@vueuse/core";
import { httpError, HttpErrorStatus } from "@/services/api/interceptors/error.ts";
import ErrorPage from "@/pages/Error/ErrorPage.vue";
import router from "@/router";

const authStore = useAuthStore();
const { isAuthenticated } = storeToRefs(authStore);
const { isCookieBanner } = storeToRefs(useUiStore());
const matomo = useMatomo();
const route = useRoute();

const error = ref<HttpErrorStatus | undefined>(undefined);
const unregisterFn = ref<() => void>(() => {});
const httpStatusBus = useEventBus(httpError);
httpStatusBus.on(({ status }) => {
  unregisterFn.value = router.beforeEach(() => {
    error.value = undefined;
  });
  error.value = status;
});

watch(error, (newValue) => {
  if (newValue === null) {
    unregisterFn.value();
    unregisterFn.value = () => {};
  }
});

tryOnBeforeMount(() => {
  // We need to use Matomo's low level window._paq API since the Vue Matomo plugin loads asynchronously
  // and is probably not available when this hook is executed (see https://www.npmjs.com/package/vue-matomo#note-on-async-loading)
  // The window._paq API works the following way:
  // - window._paq is an array that collects commands to be executed by Matomo
  // - If Matomo is not yet loaded, commands are pushed to the array
  // - Once Matomo is loaded, it processes the commands in the array in order
  //
  // For further details on the window._paq API see https://developer.matomo.org/guides/tracking-javascript-guide
  //
  // If we do more with Matomo, we should extract these low-level details into a separate class or composable.
  // @ts-ignore
  const _paq = (window._paq = window._paq || []);
  _paq.push([
    function () {
      // @ts-ignore
      const matomo = window.Matomo.getTracker();
      const consentAlreadyGivenOrDenied = matomo.hasRememberedConsent() || matomo.isUserOptedOut();
      isCookieBanner.value = !consentAlreadyGivenOrDenied;
    },
  ]);
});
</script>
<template>
  <ThemeScroller class="h-100 bg-paper-neutral">
    <PageLoader />
    <div v-show="route.name !== 'data-protection'">
      <CookieBanner v-if="isCookieBanner" />
    </div>
    <Header />
    <main class="app-main w-100 flex-fill bg-paper-neutral">
      <ErrorPage v-if="error" :code="error" />
      <RouterView v-show="!error" />
      <PageOverlay />
    </main>
    <UserBookmarksManageMediaDialog v-if="isAuthenticated" />
    <Footer />
  </ThemeScroller>
</template>

<style lang="scss">
#app {
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  background-color: var(--white);
  min-height: 100%;
}

.app-main {
  position: relative;
  scroll-margin-top: -100px;
}
</style>
